import React, { useEffect, useRef, useState } from 'react';
import { finishLoading, startLoading } from 'store/loading/actions';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import apiOffers from 'api/offers';
import IOfferProductGet from 'api/interfaces/offers/IOfferProductGet';
import translations from 'services/translations/translations';
import { ADDITIONAL_OFFER } from 'services/enums/offers/offerTypes';
import { addMessage } from 'store/messages/actions';

import getConflictMessage from 'services/offers/getConflictMessage';
import useOfferVisit from 'services/offers/useOfferVisit';
import formatToApiFormat from './src/offerProduct/formatToApiFormat';
import formatToFormStateFormat from './src/offerProduct/formatToFormStateFormat';
import getDefaultFormState from './src/offerProduct/getDefaultFormState';
import IFormState from './src/offerProduct/IFormState';
import OfferProduct from './src/offerProduct/OfferProduct';
import { useUsedPositions } from './src/offerProduct/usedPositions';
import PdfPreviewPanel from './src/pdfPreview/PdfPreviewPanel';
import 'style/offers/PdfPreviewPanel.css';
import 'style/offers/Offer.css';
import IOfferGetSingle from '../../api/interfaces/offers/IOfferGetSingle';
import { removeNavMenuText, setNavMenuText } from '../../store/navMenu/actions';

const EditOfferProduct = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation([
    translations.common,
    translations.offers,
  ]);

  const [additionalOfferId, setAdditionalOfferId] = useState<null | number>(
    null
  );
  const [locked, setLocked] = useState(false);
  // The position has been loaded, so whether it is locked is known
  const [loaded, setLoaded] = useState(false);
  const [formState, setFormState] = useState<IFormState>(getDefaultFormState());
  const [offerId, setOfferId] = useState<null | number>(null);
  const [offer, setOffer] = useState<IOfferGetSingle>();
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  // The edit version of the loaded calculation: a save is refused if another user saved
  // after it
  const [editVersion, setEditVersion] = useState<number>();
  // A save is on its way to the server
  const saving = useRef(false);
  // The calculation window has the preview button in its own button bar
  const { id, type }: { id: string | undefined; type: string } = useParams();

  useEffect(() => {
    if (offer != null) {
      // The phase of the sale before the site; a position of an additional offer is of it
      const offerType =
        type === ADDITIONAL_OFFER ? ADDITIONAL_OFFER : offer.type;
      dispatch(
        setNavMenuText(
          offer.targetName,
          '/offers',
          offerType
            ? t(`${translations.offers}:phases.${offerType}`)
            : undefined
        )
      );
    }
  }, [offer, i18n.language]);

  useEffect(() => {
    // This is triggered when component is closed
    return () => {
      dispatch(removeNavMenuText());
    };
  }, []);

  useEffect(() => {
    if (id === undefined) {
      return;
    }

    const initializeProduct = async () => {
      dispatch(startLoading('offer-product-get'));

      // A failed load (e.g. no right to the positions) must not leave the page spinning
      try {
        const existingProduct: IOfferProductGet = await apiOffers.products.get(
          parseInt(id, 10)
        );

        if (existingProduct.additionalOffer !== null) {
          setAdditionalOfferId(existingProduct.additionalOffer.id);
          setLocked(existingProduct.additionalOffer.isLocked);
        }

        if (existingProduct.offer !== null) {
          setOfferId(existingProduct.offer.id);
          setLocked(existingProduct.offer.isLocked);
        }

        setFormState(formatToFormStateFormat(existingProduct));
        setEditVersion(existingProduct.editVersion);
        setLoaded(true);

        // The offer only gives the name in the header: the position can be calculated
        // without waiting for it
        if (existingProduct.offer?.id != null) {
          apiOffers
            .getOffer(existingProduct.offer.id)
            .then(setOffer)
            .catch((error) => console.log(error));
        }
      } catch (error) {
        console.log(error);
        dispatch(
          addMessage(t(`${translations.offers}:positionErrors.Load failed`))
        );
      } finally {
        dispatch(finishLoading('offer-product-get'));
      }
    };

    initializeProduct();
  }, []);

  const cancel = () => {
    if (additionalOfferId !== null) {
      history.push(`/offers/${additionalOfferId}/${type}`);
      return;
    }

    if (offerId !== null) {
      history.push(`/offers/${offerId}/${type}`);
    }
  };

  const remove = () => {
    if (id === undefined) {
      return;
    }

    const deleteProduct = async () => {
      dispatch(startLoading('offer-product-delete'));
      try {
        await apiOffers.products.delete(parseInt(id, 10));
      } catch (error) {
        console.log(error);
        dispatch(
          addMessage(t(`${translations.offers}:positionErrors.Delete failed`))
        );
        return;
      } finally {
        dispatch(finishLoading('offer-product-delete'));
      }
      cancel();
    };

    deleteProduct();
  };

  const save = (preventRedirect: boolean) => {
    // A second save while the first is on its way would send the same version again and
    // be refused
    if (id === undefined || saving.current) {
      return;
    }

    saving.current = true;
    const postProduct = async () => {
      dispatch(startLoading('offer-product-put'));
      const product = { ...formatToApiFormat(formState), editVersion };

      try {
        if (type === 'additional-offer') {
          await apiOffers.products.putAdditionalOffer(
            parseInt(id, 10),
            product
          );
        } else {
          await apiOffers.products.put(parseInt(id, 10), product);
        }
      } catch (error) {
        console.log(error);
        dispatch(finishLoading('offer-product-put'));

        // Another user saved after this page was loaded: nothing was overwritten
        const conflict = getConflictMessage(error, t);
        if (conflict !== null) {
          dispatch(addMessage(conflict));
        } else if (error?.response?.data?.message === 'Offer is locked') {
          // The offer was sent or ordered while the position was open
          dispatch(
            addMessage(
              t(`${translations.offers}:positionErrors.Locked meanwhile`)
            )
          );
        } else if (error?.response?.status === 400) {
          dispatch(addMessage(t(`${translations.common}:Fill all fields`)));
        } else {
          dispatch(
            addMessage(t(`${translations.offers}:positionErrors.Save failed`))
          );
        }
        return;
      }

      // The save made the next version
      setEditVersion(editVersion === undefined ? undefined : editVersion + 1);
      if (!preventRedirect) {
        dispatch(finishLoading('offer-product-put'));
        cancel();
        return;
      }

      // The window stays open: a new alternative gets its id from the server, so its
      // images can be added. Read while the page still waits, so no edit is lost.
      try {
        const saved: IOfferProductGet = await apiOffers.products.get(
          parseInt(id, 10)
        );
        const savedForm = formatToFormStateFormat(saved);
        setFormState((current) => ({
          ...current,
          versions: current.versions.map((version, index) => ({
            ...version,
            id: savedForm.versions[index]?.id ?? version.id,
          })),
        }));
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(finishLoading('offer-product-put'));
      }
    };

    postProduct().finally(() => {
      saving.current = false;
    });
  };

  const previewOfferId = additionalOfferId ?? offerId;
  const offerProductId = id === undefined ? undefined : parseInt(id, 10);
  const usedPositions = useUsedPositions(
    previewOfferId,
    additionalOfferId !== null,
    offerProductId
  );

  // Others calculating the same position now. Reported once loaded, so a locked or
  // read-only position is never logged as calculated.
  const others = useOfferVisit(
    offerProductId === undefined || !loaded ? {} : { offerProductId },
    !locked
  );

  return (
    <>
      {others.length > 0 ? (
        <div className="offer-position-visitors" role="alert">
          {t(`${translations.offers}:concurrency.Position also open`, {
            names: others.map((other) => other.name).join(', '),
          })}
        </div>
      ) : null}
      <OfferProduct
        action="edit"
        cancel={cancel}
        formState={formState}
        remove={remove}
        save={save}
        setFormState={setFormState}
        usedPositions={usedPositions}
        locked={locked}
        offerProductId={offerProductId}
        offerType={type}
        openPreview={
          previewOfferId === null
            ? undefined
            : () => setPreviewIsOpen(!previewIsOpen)
        }
      />
      {previewIsOpen && previewOfferId !== null ? (
        <PdfPreviewPanel
          highlightedProductId={offerProductId}
          locked={locked}
          offerId={previewOfferId}
          onClose={() => setPreviewIsOpen(false)}
          type={type}
        />
      ) : null}
    </>
  );
};

export default EditOfferProduct;
